/* eslint-disable import/no-unresolved */
import { useEffect, useState } from 'react';

import {
  getTotalTrackerEquity,
  getTotalTradingEquity,
} from 'components/primaryHeader/partials/walletMenu/computeTotalBalance';
import { TOTAL_EQUITY_DECIMAL } from 'constants/constants';
import {
  cropAfterDecimals,
  sortBalancesBySortPriority,
  scientificToDecimalNotation,
} from 'helpers/utils';
import {
  allOpenPositionsSelector,
  trackerAssetsHoldingsSelector,
} from 'selectors/holdingsSelectors';
import {
  walletSpotPriceBySymbol,
  openPositionMarkPriceSelectorById,
} from 'selectors/priceSelectors';
import {
  enableWalletsSelector,
  portfolioMarginSelector,
  roboTradingEquitySelector,
} from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';

const useTotalAccountEquity = (props: { unformatted?: boolean }): string | number => {
  const balance = useAppSelector(enableWalletsSelector);
  const balancesArray = sortBalancesBySortPriority(balance);

  const openPositions = useAppSelector(allOpenPositionsSelector);
  const trackerHoldings = useAppSelector(trackerAssetsHoldingsSelector);
  const totalRoboStrategiesEquity = useAppSelector(roboTradingEquitySelector);
  const portfolioMarginData = portfolioMarginSelector();
  const { positions_upl: positionsUPL, asset_id: portfolioEnabledAsset } =
    portfolioMarginData?.aggregate || {};
  const [totalTradingEquity, setTotalTradingEquity] = useState<number>(0);
  const [totalTrackerEquity, setTotalTrackerEquity] = useState<number>(0);
  const [totalEquity, setTotalEquity] = useState<number>(0);

  useEffect(() => {
    const computedTotalTradingEquity = getTotalTradingEquity(
      balancesArray,
      openPositions,
      openPositionMarkPriceSelectorById,
      walletSpotPriceBySymbol,
      positionsUPL,
      portfolioEnabledAsset!
    );
    setTotalTradingEquity(computedTotalTradingEquity);
  }, [balancesArray, openPositions, portfolioEnabledAsset, positionsUPL]);

  useEffect(() => {
    const computedTotalTrackerEquity = getTotalTrackerEquity(
      trackerHoldings,
      openPositionMarkPriceSelectorById
    );
    setTotalTrackerEquity(computedTotalTrackerEquity);
  }, [trackerHoldings]);

  useEffect(() => {
    const computedTotalEquity =
      Number(totalRoboStrategiesEquity) +
      Number(totalTradingEquity) +
      Number(totalTrackerEquity);
    setTotalEquity(computedTotalEquity);
  }, [totalRoboStrategiesEquity, totalTradingEquity, totalTrackerEquity]);

  if (!Object.keys(balance).length) {
    return 0;
  }

  if (props?.unformatted) {
    return totalEquity;
  }

  const totalEquityInUsd = cropAfterDecimals(
    scientificToDecimalNotation(totalEquity),
    TOTAL_EQUITY_DECIMAL
  );

  return totalEquityInUsd;
};

export default useTotalAccountEquity;
